/**
 * useChatTransport — React hook that wraps the framework-free TransportSelector.
 *
 * Exposes the same interface as the previous useEventPolling hook so that
 * ChatInterface does not need to change its call sites.
 *
 * New capabilities surfaced to callers:
 *  - `transportName` — 'sse' | 'polling' (for diagnostics)
 *  - `notifyUserMessage` — signals the transport layer that the user sent a
 *    message (used by LongPollTransport to boost polling frequency)
 *
 * Kill switch: VITE_PARLANT_TRANSPORT = 'auto' | 'sse' | 'polling'
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { log, logError } from '../utils/logger';
import { isLayoutDebugEnabled, noteServerEvents } from '../utils/layoutDebug';
import { TransportSelector } from '../transport/TransportSelector';
import { LongPollTransport } from '../transport/LongPollTransport';
import type { TransportName } from '../transport/ChatTransport';
import type { ParlantEvent, StatusType } from '../types/parlant';

export interface UseChatTransportOptions {
  sessionId: string | null;
  serverUrl: string;
  authToken?: string;
  onEventsReceived: (events: ParlantEvent[]) => void;
  onError: (error: string) => void;
  /** External lastOffset state (persisted across mode switches). */
  externalLastOffset?: number;
  externalSetLastOffset?: React.Dispatch<React.SetStateAction<number>>;
}

export interface UseChatTransportReturn {
  /** Perceived bot status (derived from status events). */
  botStatus: StatusType;
  /** Name of the currently active transport. */
  transportName: TransportName;
  /** Signal that the user sent a message (boosts polling frequency in long-poll mode). */
  notifyUserMessage: () => void;
  /**
   * @deprecated Use notifyUserMessage — kept for backward compatibility with useChatActions.
   */
  updateLastUserMessageTime: () => void;
  /**
   * For long-polling mode, trigger an immediate poll after sending a message.
   * No-op in SSE mode (events arrive via the persistent stream).
   */
  triggerImmediatePoll: () => void;
}

export const useChatTransport = ({
  sessionId,
  serverUrl,
  authToken,
  onEventsReceived,
  onError,
  externalLastOffset,
  externalSetLastOffset,
}: UseChatTransportOptions): UseChatTransportReturn => {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [botStatus, setBotStatus] = useState<StatusType>('ready');
  const [transportName, setTransportName] = useState<TransportName>('sse');

  // Use external offset if provided, fall back to local
  const [localLastOffset, setLocalLastOffset] = useState(0);
  const hasExternal =
    externalLastOffset !== undefined && externalSetLastOffset !== undefined;
  const lastOffset = hasExternal ? externalLastOffset : localLastOffset;
  const setLastOffset = hasExternal ? externalSetLastOffset : setLocalLastOffset;

  // -------------------------------------------------------------------------
  // Transport instance (created once per serverUrl/authToken pair)
  // -------------------------------------------------------------------------

  const selectorRef = useRef<TransportSelector | null>(null);
  const lastOffsetRef = useRef<number>(lastOffset);

  // Sync offset ref whenever it changes
  useEffect(() => {
    lastOffsetRef.current = lastOffset;
  }, [lastOffset]);

  // Keep stable callback refs so the transport listeners don't become stale
  const onEventsRef = useRef(onEventsReceived);
  const onErrorRef = useRef(onError);
  useEffect(() => { onEventsRef.current = onEventsReceived; }, [onEventsReceived]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Build (or rebuild) the selector when credentials change
  useEffect(() => {
    if (selectorRef.current) {
      selectorRef.current.stop();
    }

    const selector = new TransportSelector({
      serverUrl,
      authToken,
    });

    // Forward events
    selector.on('events', (events) => {
      if (!events.length) return;

      if (isLayoutDebugEnabled()) {
        noteServerEvents('transport', events, {
          transport: selector.transportName,
        });
      }

      const maxOffset = Math.max(...events.map(e => e.offset).filter(o => typeof o === 'number'));
      if (maxOffset >= 0) {
        const newOffset = maxOffset + 1;
        lastOffsetRef.current = newOffset;
        setLastOffset(newOffset);
      }

      // Derive bot status from the last status event in the batch
      const lastStatusEvent = [...events].reverse().find(e => e.kind === 'status');
      if (lastStatusEvent) {
        const s = (lastStatusEvent.data as Record<string, unknown>)?.status as StatusType | undefined;
        if (s) setBotStatus(s);
      }

      onEventsRef.current(events);
    });

    selector.on('error', (msg) => {
      const isFatal =
        msg.includes('Session not found') ||
        msg.includes('session may have been deleted');

      if (isFatal) {
        logError('Transport fatal error:', msg);
        onErrorRef.current(msg);
        return;
      }

      // SSE connection errors trigger automatic demotion check
      if (selectorRef.current) {
        selectorRef.current.recordSseFailure(
          sessionId ?? '',
          lastOffsetRef.current
        );
      }

      log('Transport error (non-fatal):', msg);
    });

    selector.on('transportChange', (name) => {
      setTransportName(name);
      log(`Transport switched to: ${name}`);
    });

    selector.on('metric', (m) => {
      log(`Transport metric: ${m.name} = ${m.value}`);
    });

    selectorRef.current = selector;
    setTransportName(selector.transportName);

    return () => {
      selector.stop();
    };
    // Rebuild when credentials change; sessionId change is handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverUrl, authToken]);

  // Start / restart the transport when sessionId changes
  useEffect(() => {
    if (!sessionId || !selectorRef.current) return;

    log(`useChatTransport: starting for session=${sessionId}, offset=${lastOffsetRef.current}`);
    selectorRef.current.start(sessionId, lastOffsetRef.current);
    setTransportName(selectorRef.current.transportName);

    return () => {
      selectorRef.current?.stop();
    };
    // Only restart when session changes; offset changes are handled inside the transport
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // -------------------------------------------------------------------------
  // Callbacks for callers
  // -------------------------------------------------------------------------

  const notifyUserMessage = useCallback(() => {
    const inner = selectorRef.current?.['inner'] as (LongPollTransport | undefined) | undefined;
    if (inner instanceof LongPollTransport) {
      inner.notifyUserMessage();
    }
  }, []);

  const triggerImmediatePoll = useCallback(() => {
    // In SSE mode this is a no-op: events arrive via the persistent stream.
    // In long-poll mode we restart immediately at the current offset.
    const sel = selectorRef.current;
    if (!sel || !sessionId) return;
    if (sel.transportName === 'polling') {
      sel.start(sessionId, lastOffsetRef.current);
    }
  }, [sessionId]);

  return {
    botStatus,
    transportName,
    notifyUserMessage,
    updateLastUserMessageTime: notifyUserMessage,
    triggerImmediatePoll,
  };
};
